import { useEffect, useState } from 'react';
import { api, formatPKR } from '../api';
import PageHeader from '../components/PageHeader';
import ClickableStatCard from '../components/ClickableStatCard';
import OwnerCard from '../components/OwnerCard';
import { MAIN_OWNERS, CHARITY_LABEL, CHARITY_SHARE_PERCENT } from '../utils/constants';

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.getDashboard().then(setData).catch((e) => setError(e.message));
  }, []);

  if (error) return <p className="text-red-600">{error}</p>;
  if (!data) return <p className="text-lumora-text/50">Loading dashboard...</p>;

  const mainOwners = (data.ownerShares || []).filter((o) => MAIN_OWNERS.includes(o.name));

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Business overview synced from Google Sheets" />

      <h2 className="font-serif text-lg text-lumora-dark-wood mb-4">Order Overview</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
        <ClickableStatCard label="Pending Orders" value={data.pendingOrders} to="/orders" icon="◷" />
        <ClickableStatCard label="Confirmed Orders" value={data.confirmedOrders} to="/orders/confirmed" icon="✓" />
        <ClickableStatCard label="Dispatched Orders" value={data.dispatchedOrders} to="/orders?tab=completed" icon="➤" />
        <ClickableStatCard label="Delivered Orders" value={data.deliveredOrders} to="/orders?tab=completed" icon="★" />
        <ClickableStatCard label="Returned Orders" value={data.returnedOrders} to="/orders/returned" icon="↩" />
        <ClickableStatCard label="Cancelled Orders" value={data.cancelledOrders} to="/orders/cancelled" icon="✕" />
      </div>

      <h2 className="font-serif text-lg text-lumora-dark-wood mb-4">Sales & Business</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        <ClickableStatCard
          label="Total Sales"
          value={formatPKR(data.totalSales ?? data.totalRevenue)}
          to="/sales-analytics"
          accent
          icon="◉"
        />
        <ClickableStatCard label="Active Products" value={data.totalProducts} to="/products" />
        <ClickableStatCard label="Total Profit" value={formatPKR(data.totalProfit)} to="/analytics" accent />
        <ClickableStatCard label="Monthly Revenue" value={formatPKR(data.monthlyRevenue)} to="/sales-analytics" />
        <ClickableStatCard label="Unread Messages" value={data.unreadMessages ?? 0} to="/messages" icon="✉" />
      </div>

      <h2 className="font-serif text-lg text-lumora-dark-wood mb-4">Owner Finance</h2>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
        {mainOwners.map((owner) => (
          <OwnerCard key={owner.name} owner={owner} />
        ))}
      </div>
      {data.charityShare && (
        <div className="glass-card mb-8 max-w-md">
          <h3 className="font-serif font-medium text-lumora-dark-wood text-sm">
            {CHARITY_LABEL} — Charity
          </h3>
          <p className="text-xs text-lumora-text/50 mt-1">{CHARITY_SHARE_PERCENT}% of distributable profit</p>
          <p className="text-lg font-semibold text-lumora-wood mt-3">
            {formatPKR(data.charityShare.shareAmount)}
          </p>
        </div>
      )}

      {data.lowStock?.length > 0 && (
        <div className="glass-card border-amber-300/50">
          <h3 className="font-semibold text-amber-800 mb-3">Low Stock Alert</h3>
          <ul className="space-y-1 text-sm">
            {data.lowStock.map((p) => (
              <li key={p.id}>
                {p.name}: <span className="text-amber-700">{p.stock} remaining</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
